import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, Building2, FileText, Users, FolderKanban,
  ExternalLink, Edit, Trash2, Phone, Mail, Globe, Plus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ExportDropdown } from '../components/shared/PageActions';
import PanelField from '../components/shared/PanelField';
import DetailTab from '../components/shared/DetailTab';
import InfoCard from '../components/shared/InfoCard';
import CollapsiblePanel from '../components/shared/CollapsiblePanel';
import { BRAND_NAME, LOGO_URL, getDefaultCompanyInfo } from '../lib/brandConfig';
import { fetchPaymentDefaults } from '../lib/defaultData';
import { previewClientPdf, downloadClientPdf } from '../lib/infoCardPdfExport';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status badge (shared style) ─────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    lead:     { bg: '#fef3c7', color: '#92400e', border: '#f59e0b' },
    active:   { bg: '#d1fae5', color: '#065f46', border: '#10b981' },
    inactive: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  };
  const s = styles[status] || styles.lead;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}33` }}
    >
      {status}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState('overview');
  const [relatedQuotes, setRelatedQuotes] = useState([]);
  const [relatedContacts, setRelatedContacts] = useState([]);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // ── Fetch client ─────────────────────────────────────────────────────────
  const fetchClient = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${id}`);
      setClient(res.data);
    } catch (err) {
      console.error('Failed to fetch client:', err);
      if (err.response?.status === 404) navigate('/');
    }
  }, [id, navigate]);

  // ── Fetch related data when tab switches ─────────────────────────────────
  const fetchRelated = useCallback(async (tab) => {
    try {
      if (tab === 'quotes') {
        const res = await axios.get(`${API}/clients/${id}/quotes`);
        setRelatedQuotes(res.data);
      } else if (tab === 'contacts') {
        const res = await axios.get(`${API}/clients/${id}/contacts`);
        setRelatedContacts(res.data);
      } else if (tab === 'projects') {
        const res = await axios.get(`${API}/clients/${id}/projects`);
        setRelatedProjects(res.data);
      }
    } catch (err) {
      console.error(`Failed to fetch ${tab}:`, err);
    }
  }, [id]);

  useEffect(() => {
    fetchClient().finally(() => setLoading(false));
  }, [fetchClient]);

  useEffect(() => {
    if (client) fetchRelated(activeRightTab);
  }, [activeRightTab, client, fetchRelated]);

  // ── Local field change (left panel) ──────────────────────────────────────
  const handleFieldChange = (field, value) => {
    setClient(prev => ({ ...prev, [field]: value }));
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { id: _id, client_no, created_at, active_contacts_count, open_quotes_count, active_projects_count, ...updateData } = client;
      const res = await axios.put(`${API}/clients/${id}`, updateData);
      setClient(res.data);
    } catch (err) {
      console.error('Failed to save client:', err);
    }
    setSaving(false);
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clients/${id}`);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  // ── Create quote for this client ─────────────────────────────────────────
  const handleCreateQuoteForClient = async () => {
    try {
      const numRes = await axios.get(`${API}/quotes/next-number`);
      const quoteNo = numRes.data.quote_no;
      const today = new Date().toISOString().slice(0, 10);

      const payload = {
        quote_no: quoteNo,
        date: today,
        client_name: client.company_name || '',
        client_abn: client.abn || '',
        client_address: client.address || '',
        client_id: client.id || '',
        project_name: client.trading_name || '',
        project_address: '',
        contact_person: '',
        revision: '',
        company_info: getDefaultCompanyInfo(),
        categories: [],
        gst_enabled: true,
        payment_schedule: [],
        project_completion: '',
        exclusions: [],
        validity_note: '',
        general_conditions: '',
        payment_details: await fetchPaymentDefaults(),
        acceptance: { client_name: '', date: '' },
      };

      const res = await axios.post(`${API}/quotes`, payload);
      navigate(`/quote/${res.data.id}`);
    } catch (err) {
      console.error('Failed to create quote for client:', err);
    }
  };

  // ── PDF ──────────────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();
  const handlePdfPreview = () => { if (client) previewClientPdf(client, companyInfo); };
  const handlePdfDownload = () => { if (client) downloadClientPdf(client, companyInfo); };
  const handlePrint = () => window.print();

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#e5e4df' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="min-h-screen" style={{ background: '#e5e4df' }}>
      {/* Top bar */}
      <div className="bg-[#1a1a2e] py-3">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {client.company_name}
              </h1>
              <span className="text-white/50 text-xs font-mono">{client.client_no}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete Client</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {client.company_name}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Body: left panel + right content */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-6">
        <div className="flex gap-0 rounded-lg overflow-hidden shadow-xl" style={{ background: '#f8f7f4' }}>

          {/* ── Left Panel (Navy 320px) ─────────────────────────────────── */}
          <CollapsiblePanel collapsed={panelCollapsed} onToggle={() => setPanelCollapsed(!panelCollapsed)}>
                {/* Section: Company */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Company
                  </h3>
                  <PanelField label="Company Name" value={client.company_name} onChange={v => handleFieldChange('company_name', v)} />
                  <PanelField label="Trading Name" value={client.trading_name} onChange={v => handleFieldChange('trading_name', v)} />
                  <PanelField label="ABN" value={client.abn} onChange={v => handleFieldChange('abn', v)} />
                  <PanelField
                    label="Industry"
                    value={client.industry}
                    onChange={v => handleFieldChange('industry', v)}
                    type="select"
                    options={[
                      { value: '', label: '— Select —' },
                      { value: 'hospitality', label: 'Hospitality' },
                      { value: 'retail', label: 'Retail' },
                      { value: 'commercial', label: 'Commercial' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                </div>

                {/* Section: Status */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Status
                  </h3>
                  <PanelField
                    label="Status"
                    value={client.status}
                    onChange={v => handleFieldChange('status', v)}
                    type="select"
                    options={[
                      { value: 'lead', label: 'Lead' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                  />
                </div>

                {/* Section: Location */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Location
                  </h3>
                  <PanelField label="Address" value={client.address} onChange={v => handleFieldChange('address', v)} />
                  <PanelField label="Website" value={client.website} onChange={v => handleFieldChange('website', v)} />
                </div>

                {/* Section: Notes */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Notes
                  </h3>
                  <PanelField label="Notes" value={client.notes} onChange={v => handleFieldChange('notes', v)} type="textarea" />
                </div>
          </CollapsiblePanel>

          {/* ── Right Content ────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Right tabs */}
            <div
              className="flex border-b px-4"
              style={{ borderColor: 'rgba(201,168,76,0.15)' }}
            >
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'quotes', label: `Quotes (${relatedQuotes.length})` },
                { key: 'contacts', label: `Contacts (${relatedContacts.length})` },
                { key: 'projects', label: `Projects (${relatedProjects.length})` },
              ].map(t => (
                <DetailTab key={t.key} active={activeRightTab === t.key} onClick={() => setActiveRightTab(t.key)} label={t.label} />
              ))}
            </div>

            {/* Tab content */}
            <div className="p-6">
              {activeRightTab === 'overview' && (
                <OverviewTab client={client} />
              )}
              {activeRightTab === 'quotes' && (
                <QuotesSubTab
                  quotes={relatedQuotes}
                  client={client}
                  onEdit={qId => navigate(`/quote/${qId}`)}
                  onCreateQuote={() => handleCreateQuoteForClient()}
                />
              )}
              {activeRightTab === 'contacts' && (
                <ContactsSubTab contacts={relatedContacts} onEdit={cId => navigate(`/contacts/${cId}`)} />
              )}
              {activeRightTab === 'projects' && (
                <ProjectsSubTab projects={relatedProjects} onEdit={pId => navigate(`/projects/${pId}`)} />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────
function OverviewTab({ client }) {
  const statusStyles = {
    lead:     { bg: '#fef3c7', color: '#92400e' },
    active:   { bg: '#d1fae5', color: '#065f46' },
    inactive: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const ss = statusStyles[client.status] || statusStyles.lead;

  return (
    <div className="space-y-6">
      {/* Info card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InfoCard icon={Building2} label="Company" value={client.company_name} sub={client.trading_name} />
        <InfoCard icon={Building2} label="Status" value={<StatusBadge status={client.status} />} />
        <InfoCard icon={Building2} label="ABN" value={client.abn || '—'} mono />
        <InfoCard icon={Building2} label="Industry" value={client.industry || '—'} />
        <InfoCard icon={Building2} label="Address" value={client.address || '—'} />
        {client.website && <InfoCard icon={Globe} label="Website" value={<a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{client.website}</a>} />}
      </div>

      {/* Notes */}
      {client.notes && (
        <div>
          <h3 className="text-sm font-semibold text-[#1a1a2e] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Notes</h3>
          <div className="p-4 rounded-md text-sm text-[#2c2c2c] whitespace-pre-wrap" style={{ background: '#f3f2ef', border: '1px solid #e5e4df' }}>
            {client.notes}
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="flex gap-4 text-xs text-[#9ca3af]">
        <span>Created: {client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'}</span>
        <span>Updated: {client.updated_at ? new Date(client.updated_at).toLocaleDateString() : '—'}</span>
      </div>
    </div>
  );
}

// ── Quotes sub-tab ───────────────────────────────────────────────────────────
function QuotesSubTab({ quotes, client, onEdit, onCreateQuote }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-[#6b7280]">{quotes.length} quote{quotes.length !== 1 ? 's' : ''}</span>
        <button
          onClick={onCreateQuote}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
          style={{ background: '#1a1a2e', color: '#c9a84c' }}
        >
          <Plus className="w-3.5 h-3.5" />
          New Quote
        </button>
      </div>
      {quotes.length === 0 ? (
        <EmptyState icon={FileText} message="No quotes linked to this client" />
      ) : (
        <div className="space-y-1">
          <div className="hidden sm:grid gap-3 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]" style={{ gridTemplateColumns: '100px 100px 1fr 100px' }}>
            <span>Quote No</span><span>Date</span><span>Project</span><span className="text-right">Actions</span>
          </div>
          {quotes.map(q => (
            <div key={q.id} className="grid gap-3 px-3 py-2 rounded hover:bg-[#f3f2ef] cursor-pointer items-center" style={{ gridTemplateColumns: '100px 100px 1fr 100px', borderBottom: '1px solid rgba(201,168,76,0.08)' }} onClick={() => onEdit(q.id)}>
              <span className="font-mono text-sm font-semibold text-[#1a1a2e]">{q.quote_no}</span>
              <span className="text-sm text-[#6b7280]">{q.date || '—'}</span>
              <span className="text-sm truncate">{q.project_name || '—'}</span>
              <div className="text-right" onClick={e => e.stopPropagation()}>
                <button onClick={() => onEdit(q.id)} className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e]"><Edit className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Contacts sub-tab ─────────────────────────────────────────────────────────
function ContactsSubTab({ contacts, onEdit }) {
  if (contacts.length === 0) {
    return <EmptyState icon={Users} message="No contacts linked to this client" />;
  }
  return (
    <div className="space-y-1">
      {contacts.map(c => (
        <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded hover:bg-[#f3f2ef] cursor-pointer" style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }} onClick={() => onEdit(c.id)}>
          <div>
            <span className="text-sm font-medium text-[#1a1a2e]">{c.full_name}</span>
            {c.title && <span className="text-xs text-[#6b7280] ml-2">{c.title}</span>}
          </div>
          <div className="flex items-center gap-2">
            {c.email && <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="p-1 rounded hover:bg-blue-50 text-[#6b7280] hover:text-blue-600"><Mail className="w-4 h-4" /></a>}
            {c.phone && <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} className="p-1 rounded hover:bg-green-50 text-[#6b7280] hover:text-green-600"><Phone className="w-4 h-4" /></a>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Projects sub-tab ─────────────────────────────────────────────────────────
function ProjectsSubTab({ projects, onEdit }) {
  if (projects.length === 0) {
    return <EmptyState icon={FolderKanban} message="No projects linked to this client" />;
  }
  return (
    <div className="space-y-1">
      {projects.map(p => (
        <div key={p.id} className="flex items-center justify-between px-3 py-2 rounded hover:bg-[#f3f2ef] cursor-pointer" style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }} onClick={() => onEdit(p.id)}>
          <div>
            <span className="text-sm font-medium text-[#1a1a2e]">{p.project_name}</span>
            <span className="text-xs font-mono text-[#6b7280] ml-2">{p.project_no}</span>
          </div>
          <StatusBadge status={p.status} />
        </div>
      ))}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ icon: Icon, message }) {
  return (
    <div className="text-center py-12">
      <Icon className="w-12 h-12 mx-auto text-[#c9a84c]/30 mb-3" />
      <p className="text-[#6b7280] text-sm">{message}</p>
    </div>
  );
}
